import React, { useState, useLayoutEffect, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './SignIn.css';

/** After sign-in we always land on /search (never /). Optionally keep map addresses from router state. */
function postSignInNavigateOptions(locationState) {
  if (!locationState || typeof locationState !== 'object') {
    return { replace: true };
  }
  const next = {};
  if (locationState.workingAddress) next.workingAddress = locationState.workingAddress;
  if (locationState.stayingAddress) next.stayingAddress = locationState.stayingAddress;
  return Object.keys(next).length ? { replace: true, state: next } : { replace: true };
}

export default function SignIn() {
  const { signInWithGoogle, signInWithEmail, signUpWithEmail, isSignedIn, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const postAuthOpts = useMemo(() => postSignInNavigateOptions(location.state), [location.state]);

  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useLayoutEffect(() => {
    document.body.classList.add('page-signin');
    return () => document.body.classList.remove('page-signin');
  }, []);

  // Wait for auth to settle (including sign-out in progress). Redirecting while still
  // "signed in" briefly sent users away before the form ever painted.
  useEffect(() => {
    if (authLoading) return;
    if (isSignedIn) navigate('/search', postAuthOpts);
  }, [authLoading, isSignedIn, navigate, postAuthOpts]);

  async function handleGoogleClick() {
    setError('');
    try {
      await signInWithGoogle();
      navigate('/search', postAuthOpts);
    } catch (err) {
      setError(err.message || 'Google sign-in failed.');
    }
  }

  async function handleEmailSubmit(e) {
    e.preventDefault();
    setError('');
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setError('Please enter email and password.');
      return;
    }
    setSubmitting(true);
    try {
      if (mode === 'signup') {
        await signUpWithEmail(trimmedEmail, password);
      } else {
        await signInWithEmail(trimmedEmail, password);
      }
      navigate('/search', postAuthOpts);
    } catch (err) {
      setError(err.message || (mode === 'signup' ? 'Sign up failed.' : 'Sign in failed.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="signin-page">
      <div className="signin-page__card">
        <h1 className="signin-page__title">Sign In / Sign Up</h1>
        <p className="signin-page__subtitle">
          Sign in to save favorite locations and use them on the map.
        </p>

        <button
          type="button"
          className="signin-page__google"
          onClick={handleGoogleClick}
        >
          Continue with Google
        </button>

        <div className="signin-page__divider">
          <span>or</span>
        </div>

        <form className="signin-page__form" onSubmit={handleEmailSubmit}>
          <div className="signin-page__tabs">
            <button
              type="button"
              className={`signin-page__tab ${mode === 'signin' ? 'signin-page__tab--active' : ''}`}
              onClick={() => { setMode('signin'); setError(''); }}
            >
              Sign in
            </button>
            <button
              type="button"
              className={`signin-page__tab ${mode === 'signup' ? 'signin-page__tab--active' : ''}`}
              onClick={() => { setMode('signup'); setError(''); }}
            >
              Create account
            </button>
          </div>

          <label className="signin-page__label" htmlFor="signin-email">
            Email
          </label>
          <input
            id="signin-email"
            type="email"
            className="signin-page__input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            placeholder="you@example.com"
          />

          <label className="signin-page__label" htmlFor="signin-password">
            Password
          </label>
          <input
            id="signin-password"
            type="password"
            className="signin-page__input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            placeholder={mode === 'signup' ? 'At least 6 characters' : 'Your password'}
          />

          {error && (
            <p className="signin-page__error" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            className="signin-page__submit"
            disabled={submitting}
          >
            {submitting ? 'Please wait…' : mode === 'signup' ? 'Create account' : 'Sign in'}
          </button>
        </form>

        <p className="signin-page__back">
          <Link to="/" className="signin-page__back-link">
            ← Back to home
          </Link>
        </p>
      </div>
    </div>
  );
}
